import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Input, message, Modal, Radio, Row, Select } from 'antd';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';

import DateTimePicker from 'components/DateTimePicker';
import ModalFormFooter from 'components/Form/ModalFormFooter';
import { FormItem } from 'components/Layout/Form';
import TimezonePicker from 'components/TimezonePicker';
import { makeSelectTenantTimezone } from 'containers/App/selectors';
import { formItemLayout } from 'containers/BDO/utils';

import { createBDOSetting, getBDOSetting, updateBDOSetting } from '../action';
import { creditCardsGet } from '../creditCard/actions';
import {
  IBDOSetting,
  IBDOSettingsState,
  ICreditCard,
  AddEditMemFeeWaiverModalProps,
  CreateBDOSettingFormValues,
  CreateBDOSettingPayload,
  UpdateBDOSettingPayload
} from '../types';

interface FormValues extends CreateBDOSettingFormValues {}

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  cost: yup
    .number()
    .typeError('Cost must be a number')
    .integer('Cost must be an integer')
    .min(1, 'Cost must be greater than 0')
    .required('Cost is required'),
  allow_supplementary: yup.boolean(),
  card_ids: yup
    .array()
    .of(yup.number())
    .min(1, 'At least one credit card must be selected')
    .required('Credit Card is required'),
  supplementary_cost: yup
    .string()
    .nullable()
    .when('allow_supplementary', {
      is: true,
      then: () => yup.string().required('Supplementary Card Cost is required')
    }),
  lrs_reward_code: yup
    .string()
    .matches(/^[a-zA-Z0-9]+$/, 'LRS Reward Code must contain only letters and numbers')
    .required('LRS Reward Code is required'),
  lrs_reward_code_supp: yup
    .string()
    .nullable()
    .when('allow_supplementary', {
      is: true,
      then: schema =>
        schema
          .matches(
            /^[a-zA-Z0-9]+$/,
            'LRS Reward Code (Supp.) must contain only letters and numbers'
          )
          .required('LRS Reward Code (Supp.) is required')
    }),
  start_date: yup
    .date()
    .nullable()
    .required('Start Date is required')
    .typeError('Invalid Start Date'),
  end_date: yup
    .date()
    .nullable()
    .required('End Date is required')
    .typeError('Invalid End Date')
    .min(yup.ref('start_date'), 'End Date must be after Start Date')
});

const AddEditMemFeeWaiverModal: React.FC<AddEditMemFeeWaiverModalProps> = ({
  selectedMemFeeWaiver = null,
  modalOpen,
  isEdit = false,
  closeModal
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchedBDOSettingData: IBDOSetting | null = useSelector(
    (state: { bdoSettings: IBDOSettingsState }) => state.bdoSettings.bdoSetting
  );
  const { creditCards } = useSelector((state: any) => state.creditCards);
  const tenantTimezone: string = useSelector(makeSelectTenantTimezone()) || 'UTC';

  useEffect(() => {
    if (modalOpen) {
      dispatch(creditCardsGet({ size: 200 }));
    }
  }, [modalOpen, dispatch]);

  useEffect(() => {
    if (modalOpen && selectedMemFeeWaiver && isEdit) {
      dispatch(getBDOSetting(selectedMemFeeWaiver));
    }
  }, [modalOpen, selectedMemFeeWaiver, isEdit, dispatch]);

  const initialValues: FormValues = {
    name:
      isEdit && fetchedBDOSettingData
        ? fetchedBDOSettingData?.name
        : !isEdit && selectedMemFeeWaiver
        ? `Copy of ${fetchedBDOSettingData?.name}`
        : '',
    cost: fetchedBDOSettingData?.cost || '',
    allow_supplementary: fetchedBDOSettingData?.allow_supplementary ?? true,
    supplementary_cost: fetchedBDOSettingData?.allow_supplementary
      ? fetchedBDOSettingData?.supplementary_cost || ''
      : '',
    lrs_reward_code: fetchedBDOSettingData?.lrs_reward_code || '',
    lrs_reward_code_supp: fetchedBDOSettingData?.lrs_reward_code_supp || '',
    card_ids: fetchedBDOSettingData?.cards?.map((card: ICreditCard) => card.id) || [],
    start_date: fetchedBDOSettingData?.start_date
      ? new Date(fetchedBDOSettingData?.start_date)
      : null,
    end_date: fetchedBDOSettingData?.end_date ? new Date(fetchedBDOSettingData?.end_date) : null,
    item_type: 'membership_fee'
  };

  const cardOptions =
    creditCards?.data?.map(card => ({
      label: card.product_code,
      value: card.id
    })) || [];

  const onSubmit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    const { setSubmitting, resetForm } = formikHelpers;

    const onSuccess = () => {
      setSubmitting(false);
      resetForm();
      closeModal();
      if (!isEdit) {
        navigate(`/p/credit_card_redemption_settings/mem-fee-waiver/list`);
      }
    };

    const onError = () => {
      setSubmitting(false);
    };
    try {
      const apiValues: CreateBDOSettingPayload | UpdateBDOSettingPayload = {
        ...values,
        supplementary_cost: values.allow_supplementary ? values.supplementary_cost : null,
        lrs_reward_code_supp: values.allow_supplementary ? values.lrs_reward_code_supp : null,
        start_date: values.start_date
          ? typeof values.start_date === 'string'
            ? values.start_date
            : values.start_date.toISOString()
          : '',
        end_date: values.end_date
          ? typeof values.end_date === 'string'
            ? values.end_date
            : values.end_date.toISOString()
          : '',
        item_type: 'membership_fee' as 'membership_fee'
      };

      if (isEdit && selectedMemFeeWaiver) {
        dispatch(
          updateBDOSetting(
            {
              ...(apiValues as UpdateBDOSettingPayload),
              id: selectedMemFeeWaiver
            },
            onSuccess,
            onError
          )
        );
      } else {
        dispatch(createBDOSetting(apiValues as CreateBDOSettingPayload, onSuccess, onError));
        navigate(`/p/credit_card_redemption_settings/mem-fee-waiver/list`);
      }

      resetForm();
      closeModal();
    } catch (error) {
      message.error('Failed to save BDO setting.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      destroyOnClose
      open={modalOpen}
      title="Enter Mem Fee Waiver Details"
      footer={null}
      onCancel={closeModal}
      width={1000}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({ isSubmitting, resetForm, setFieldValue, values }) => {
          return (
            <Form>
              <Field name={'name'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Name'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input allowClear placeholder={'Mem Fee Waiver Program Name'} {...field} />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'cost'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Cost'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        precision={0}
                        allowClear
                        placeholder={'Enter the cost'}
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <FormItem {...formItemLayout} label="Supp. Card Allowed" required>
                <Field name="allow_supplementary">
                  {({ field }) => (
                    <Radio.Group
                      {...field}
                      onChange={e => {
                        const { value } = e.target;
                        setFieldValue(field.name, value);
                        if (!value) {
                          setFieldValue('supplementary_cost', '');
                          setFieldValue('lrs_reward_code_supp', '');
                        }
                      }}
                      value={field.value}
                    >
                      <Radio.Button value={true}>Yes</Radio.Button>
                      <Radio.Button value={false}>No</Radio.Button>
                    </Radio.Group>
                  )}
                </Field>
              </FormItem>
              <Field name={'supplementary_cost'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Supp. Card Cost'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required={values.allow_supplementary}
                    >
                      <Row align="middle" gutter={16}>
                        <Col>
                          <Input
                            type="number"
                            min={0}
                            step="1"
                            precision={0}
                            allowClear
                            placeholder={'Enter the cost'}
                            {...field}
                            disabled={!values.allow_supplementary}
                          />
                        </Col>
                      </Row>
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'lrs_reward_code'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'LRS Reward Code (Main)'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Input
                        allowClear
                        placeholder={
                          'Enter LRS Reward Code for Primary Cards (not visible to customers)'
                        }
                        {...field}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'lrs_reward_code_supp'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'LRS Reward Code (Supp.)'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required={values.allow_supplementary}
                    >
                      <Input
                        allowClear
                        placeholder={
                          'Enter LRS Reward Code for Supplementary Cards (not visible to customers)'
                        }
                        {...field}
                        disabled={!values.allow_supplementary}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <Field name={'card_ids'}>
                {({ field, meta }) => {
                  const hasError = meta.error && meta.touched;
                  return (
                    <FormItem
                      {...formItemLayout}
                      label={'Applicable Credit Cards'}
                      validateStatus={hasError ? 'error' : undefined}
                      hasFeedback={!hasError && meta.touched}
                      help={hasError && <>{meta.error}</>}
                      required
                    >
                      <Select
                        {...field}
                        allowClear
                        style={{ minWidth: 300, maxWidth: 800 }}
                        size="large"
                        mode="multiple"
                        options={cardOptions}
                        onChange={value => {
                          setFieldValue(field.name, value);
                        }}
                      />
                    </FormItem>
                  );
                }}
              </Field>
              <FormItem {...formItemLayout} label="Start date" required>
                <Row gutter={16} align="middle">
                  <Field name="start_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('start_date', value)}
                            datePickerId="start_date_date_picker"
                            timePickerId="start_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <FormItem {...formItemLayout} label="End date" required>
                <Row gutter={16} align="middle">
                  <Field name="end_date">
                    {({ field, form, meta }) => {
                      const hasError = meta.error && meta.touched;
                      return (
                        <FormItem
                          validateStatus={hasError ? 'error' : undefined}
                          hasFeedback={!hasError && meta.touched}
                          help={hasError && <>{meta.error}</>}
                          style={{ marginBottom: 0 }}
                        >
                          <DateTimePicker
                            value={field.value}
                            onChange={value => form.setFieldValue('end_date', value)}
                            datePickerId="end_date_date_picker"
                            timePickerId="end_date_time_picker"
                            timezone={tenantTimezone}
                            showDate
                            showTime
                          />
                        </FormItem>
                      );
                    }}
                  </Field>
                  <Col span={8}>
                    <TimezonePicker value={tenantTimezone} disabled={true} />
                  </Col>
                </Row>
              </FormItem>
              <ModalFormFooter>
                <Button
                  type="default"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                >
                  Cancel
                </Button>
                <Button htmlType="submit" loading={isSubmitting} type="primary">
                  Save
                </Button>
              </ModalFormFooter>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default AddEditMemFeeWaiverModal;
